<template>
  <div>
    <div class="action-key-container">
      <label class="tb-label">
        Hot Keys
      </label>

      <p>
        Trigger by pressing buttons or using keyboard shortcuts
      </p>

      <div class="action-keys">
        <action-key
          v-for="(action, index) in actions"
          :key="index"
          :action="action"
          :production_context="production_context"
          :data-test="`graphics-editor-ticker-${action.hotkey.toLowerCase()}`"
        />
      </div>
    </div>

    <div class="tb-form-group">
      <label class="tb-label">
        Video
      </label>

      <div v-if="src" class="media-upload-wrapper media-upload-wrapper--video">
        <video class="media" :src="src" :type="contentType" ref="videoPreview" data-test="graphics-editor-video-preview"></video>

        <div class="edit-media">
          <button
            class="tb-btn tb-btn-contained on-image"
            data-entity="producer-editor-select"
            data-test="graphics-editor-video-select"
            @click="selectAsset"
          >
            <span class="fas fa-pencil-alt"></span>
          </button>
        </div>
      </div>

      <button
        v-else
        class="tb-btn tb-btn-contained medium-em"
        data-entity="producer-editor-selectVideo"
        data-test="graphics-editor-video-select"
        @click="selectAsset"
      >
        <span class="fas fa-image mr-1"></span>
        Choose Video
      </button>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleAutoplay">
        <input type="checkbox" id="autoplay" data-test="graphics-editor-video-autoplay" v-model="autoplay">
        <label for="autoplay">Autoplay</label>
      </div>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleLoop">
        <input type="checkbox" id="loop" data-test="graphics-editor-video-loop" v-model="loop">
        <label for="loop">Loop</label>
      </div>
    </div>

    <div class="tb-form-group">
      <div class="tb-checkbox" data-entity="producer-editor-toggleMute">
        <input type="checkbox" id="muted" data-test="graphics-editor-video-mute" v-model="muted">
        <label for="muted">Start Muted</label>
      </div>
    </div>

    <div class="tb-form-group" role="group" aria-labelledby="in-out-label">
      <div id="in-out-label">In/Out</div>

      <div class="tb-range-slider my-2">
        <input
          class="tb-range-slider__input"
          type="range"
          :min="0"
          :step="0.1"
          :max="Math.ceil(currentVideoDuration)"
          v-model.number="startTimeInSeconds"
          data-entity="producer-editor-startTimeInSeconds"
          data-test="graphics-editor-video-startTimeSlider"
        />

        <input
          class="tb-range-slider__input"
          type="range"
          :min="0"
          :step="0.1"
          :max="Math.ceil(currentVideoDuration)"
          v-model.number="endTimeInSeconds"
          data-entity="producer-editor-endTimeInSeconds"
          data-test="graphics-editor-video-endTimeSlider"
        />
      </div>
    </div>

    <div class="flex-row scrubber">
      <div class="input-group">
        <input
          v-model.lazy="startTimeFormatted"
          class="tb-input text-center"
          type="text"
          name="inTime"
          data-entity="producer-editor-startTimeFormatted"
          data-test="graphics-editor-video-startTimeInput"
        >

        <label class="tb-label">In</label>
      </div>

      <div class="input-group ml-1">
        <input
          v-model.lazy="endTimeFormatted"
          class="tb-input text-center"
          type="text"
          name="outTime"
          data-entity="producer-editor-endTimeFormatted"
          data-test="graphics-editor-video-endTimeInput"
        >

        <label class="tb-label">Out</label>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import ActionKey from '../../../panel-editor/hotkeys/ActionKey.vue';

dayjs.extend(duration);

export default {
  props: ['element', 'graphic_fields', 'production_context'],

  components: {
    ActionKey,
  },

  computed: {

    actions() {
      return [
        {
          hotkey: 'P',
          display: 'Play/Pause',
          description: 'Use this hotkey to pause the video',
          value: 'play/pause',
          type: 'video',
          elementId: this.element.id,
        },
        {
          hotkey: 'M',
          display: 'Mute',
          description: 'Use this hotkey to mute the video',
          value: 'mute',
          type: 'video',
          elementId: this.element.id,
        },
      ];
    },

    currentVideoDuration() {
      return this.elementFields?.duration;
    },

    currentStartTime() {
      return this.elementFields?.videoOptions?.start;
    },

    currentEndTime() {
      return this.elementFields?.videoOptions?.end;
    },

    elementFields() {
      return this.graphic_fields[this.element.id];
    },

    autoplay: {
      get() {
        return this.elementFields?.videoOptions?.autoplay;
      },
      set(v) {
        this.updateField('videoOptions.autoplay', v);
      },
    },

    loop: {
      get() {
        return this.elementFields?.videoOptions?.loop;
      },
      set(v) {
        this.updateField('videoOptions.loop', v);
      },
    },

    muted: {
      get() {
        return this.elementFields?.videoOptions?.muted;
      },
      set(v) {
        this.updateField('videoOptions.muted', v);
      },
    },

    src() {
      return this.elementFields?.src;
    },

    contentType() {
      return this.elementFields?.contentType;
    },

    startTimeInSeconds: {
      get() {
        return this.currentStartTime;
      },

      set(v) {
        let newStart = v;

        if (v < 0) {
          newStart = 0;
        } else if (v >= this.currentVideoDuration) {
          newStart = this.currentVideoDuration;
          this.updateField('videoOptions.end', false);
        }

        if (v > this.endTimeInSeconds) {
          this.updateField('videoOptions.end', newStart);
        }

        this.updateField('videoOptions.start', newStart);
      },
    },

    startTimeFormatted: {
      get() {
        return dayjs.duration(this.startTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.startTimeInSeconds = this.convertToSeconds(v);
      },
    },

    endTimeInSeconds: {
      get() {
        const endValue = this.currentEndTime;
        if (endValue === false) {
          return Math.ceil(this.currentVideoDuration);
        }

        return endValue;
      },

      set(v) {
        if (v >= this.currentVideoDuration) {
          this.updateField('videoOptions.end', false);
        } else {
          this.updateField('videoOptions.end', v);
        }

        if (v < this.startTimeInSeconds) {
          this.updateField('videoOptions.start', v);
        }
      },
    },

    endTimeFormatted: {
      get() {
        return dayjs.duration(this.endTimeInSeconds * 1000).format('m:ss');
      },
      set(v) {
        this.endTimeInSeconds = this.convertToSeconds(v);
      },
    },
  },
  watch: {
    startTimeInSeconds(time) {
      this.setTime(time);
    },
    endTimeInSeconds(time) {
      this.setTime(time);
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit('value', { field, value });
    },

    setTime(t) {
      if (this.$refs.videoPreview) {
        this.$refs.videoPreview.currentTime = t;
      }
    },

    convertToSeconds(input) {
      const digitStr = input.replace(/\D/g, ''); // remove non digit chars
      const digitArr = digitStr.split('');

      return digitArr.reverse().reduce((total, d, idx) => {
        const numD = Number(d);

        switch (idx) {
          case 1: // 10s seconds
            return total + (numD * 10);
          case 2: // minutes
            return total + (numD * 60);
          case 3: // 10s minutes
            return total + (numD * 600);

            // TODO: support for longer than 1 hour?

          default:
            return total + numD;
        }
      }, 0);
    },

    selectAsset() {
      this.$store.dispatch('toggleAssetModal', { toggled: true, type: 'video' });

      window.assetmanfn = (asset, meta = {}) => {
        const mediaMeta = { ...asset.meta, ...meta };
        this.element.thumbnailUrl = asset.thumbnail_url; // eslint-disable-line

        this.updateField('thumbnailUrl', asset.thumbnail_url);
        this.updateField('src', asset.media_url);
        this.updateField('media_url', asset.media_url);
        this.updateField('duration', mediaMeta.duration);
        this.updateField('contentType', mediaMeta.content_type);
        this.updateField('mediaMeta', mediaMeta);
        this.updateField('videoOptions', {
          ...this.element.videoOptions,
          start: 0,
          end: false,
        });

        this.$store.dispatch('toggleAssetModal', { toggled: false, type: 'video' });
      };
    },
  },
  mounted() {
    // patch element fields on mount and not the hacky computed way.
    // When element is new the field data is never set in the graphic_fields object
    // because we then saved video elements with empty fields, we also have to patch retroactively
    // otherwise graphic fields get set to live with missing fields and evaluate to falsy
    // this causes errors in play states that seem random and hard to debug
    const {
      src = this.element.src,
      duration: elementDuration = this.element.duration,
      contentType = this.element.contentType,
      videoOptions: {
        autoplay = this.element.videoOptions.autoplay,
        loop = this.element.videoOptions.loop,
        muted = this.element.videoOptions.muted,
        start = this.element.videoOptions.start,
        end = this.element.videoOptions.end,
      } = {},
    } = this.elementFields || {};

    // Create the fields object with merged values
    const fields = {
      src,
      duration: elementDuration,
      contentType,
      videoOptions: {
        autoplay,
        loop,
        muted,
        start,
        end,
      },
    };

    // Update fields using the updateField method
    this.updateField('videoOptions', fields.videoOptions);
    this.updateField('src', fields.src);
    this.updateField('duration', fields.duration);
    this.updateField('contentType', fields.contentType);
  },
};
</script>
